import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Avatar, Button, Card, Dropdown, Form, Input, Modal, Segmented, Table, Tag, Tooltip, Typography } from 'antd';
import { CopyOutlined, DeleteOutlined, EllipsisOutlined, InboxOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { SequenceItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { NameCell, TableSearch } from '../../components/NameCell';
import { EmptyState, tableLoading, tableLocale } from '../../components/states';
import { useProjectColumn } from '../../components/ProjectSelect';
import { useProjectScope } from '../../features/projects/scope';
import { errorMessage } from '../../api/client';
import { pctOf, useCreateSequence, useDeleteSequence, useDuplicateSequence, useSequenceStatus, useSequences } from '../../features/sequences/api';
import { fmtDateTime, plural } from '../../utils/format';

export const SEQUENCE_STATUS_COLOR: Record<string, string> = { DRAFT: 'default', ACTIVE: 'processing', PAUSED: 'warning', ARCHIVED: 'default' };
export const sequenceStatusLabel = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

export function SequencesPage() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const sequences = useSequences();
  const create = useCreateSequence();
  const duplicate = useDuplicateSequence();
  const remove = useDeleteSequence();
  const status = useSequenceStatus();
  const scope = useProjectScope();
  const projectColumn = useProjectColumn<SequenceItem>();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'draft' | 'paused' | 'archived'>('all');
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<{ name: string }>();

  const rows = useMemo(() => {
    const needle = q.toLowerCase();
    return (sequences.data ?? []).filter((s) => {
      if (needle && !s.name.toLowerCase().includes(needle)) return false;
      if (filter === 'all') return s.status !== 'ARCHIVED';
      return s.status === filter.toUpperCase();
    });
  }, [sequences.data, q, filter]);

  const submit = async () => {
    const { name } = await form.validateFields();
    try {
      const s = await create.mutateAsync({ name, projectId: scope.enabled ? (scope.current?.id ?? null) : undefined });
      setCreating(false);
      form.resetFields();
      navigate(`/cold-email/${s.id}`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const filtered = !!q || filter !== 'all';
  return (
    <div className="page">
      <PageHeader
        title="Cold Emailing"
        subtitle="Personal, plain-text style emails with automatic follow-ups, sent one by one from your agents' mailboxes. Replies land in the Inbox."
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>New sequence</Button>}
      />
      <Card>
        <div className="table-toolbar">
          <div className="toolbar-group">
            <TableSearch value={q} onSearch={setQ} placeholder="Search sequences" />
          </div>
          <div className="toolbar-group">
            <Segmented
              value={filter}
              onChange={(v) => setFilter(v as typeof filter)}
              options={[{ value: 'all', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'draft', label: 'Drafts' }, { value: 'paused', label: 'Paused' }, { value: 'archived', label: 'Archived' }]}
            />
          </div>
        </div>
        <Table<SequenceItem>
          rowKey="id"
          className="table-tight"
          loading={tableLoading(sequences)}
          locale={tableLocale(
            sequences,
            filtered ? (
              <EmptyState description="No sequences match this search or filter." action={<Button onClick={() => { setQ(''); setFilter('all'); }}>Clear search and filter</Button>} />
            ) : (
              <EmptyState description="No sequences yet. Write a few short emails, pick who gets them and which agents send them, and the follow-ups run on their own." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>New sequence</Button>} />
            ),
          )}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 1000 }}
          tableLayout="fixed"
          onRow={(s) => ({ onClick: () => navigate(`/cold-email/${s.id}`), style: { cursor: 'pointer' } })}
          columns={[
            {
              title: 'Sequence',
              dataIndex: 'name',
              width: 220,
              render: (v: string, s) => <NameCell icon={<ThunderboltOutlined />} size={32} lines={2} name={v} sub={`${plural(s.steps.length, 'step')} · ${plural(s.steps.filter((x) => x.type === 'EMAIL').length, 'email')}`} to={`/cold-email/${s.id}`} />,
            },
            ...projectColumn.map((col) => ({ ...col, width: 90 })),
            { title: 'Status', dataIndex: 'status', width: 90, render: (v: string) => <Tag color={SEQUENCE_STATUS_COLOR[v]}>{sequenceStatusLabel(v)}</Tag> },
            {
              title: 'Agents',
              key: 'agents',
              width: 100,
              render: (_, s) =>
                s.agents.length ? (
                  <Avatar.Group max={{ count: 3 }} size="small">
                    {s.agents.map((a) => (
                      <Tooltip key={a.id} title={`${a.name} <${a.email}>`}>
                        <Avatar src={a.avatarUrl ?? undefined} style={{ background: '#722ed1' }}>{a.name[0]}</Avatar>
                      </Tooltip>
                    ))}
                  </Avatar.Group>
                ) : (
                  <Typography.Text type="secondary">None</Typography.Text>
                ),
            },
            { title: 'People', key: 'people', width: 90, render: (_, s) => <Tooltip title={`${s.stats.active} active, ${s.stats.completed} finished`}>{s.stats.enrolled}</Tooltip> },
            { title: 'Sent', key: 'sent', width: 70, render: (_, s) => s.stats.sent || '—' },
            { title: 'Opened', key: 'o', width: 72, render: (_, s) => pctOf(s.stats.opened, s.stats.sent) },
            { title: 'Clicked', key: 'c', width: 72, render: (_, s) => pctOf(s.stats.clicked, s.stats.sent) },
            { title: 'Replied', key: 'r', width: 72, render: (_, s) => <Typography.Text strong={s.stats.replied > 0}>{pctOf(s.stats.replied, s.stats.contacted)}</Typography.Text> },
            { title: 'Edited', dataIndex: 'updatedAt', width: 120, render: (v: string) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{fmtDateTime(v)}</Typography.Text> },
            {
              key: 'actions',
              align: 'right',
              width: 42,
              render: (_, s) => (
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  menu={{
                    items: [
                      s.status === 'ACTIVE' ? { key: 'pause', icon: <PauseCircleOutlined />, label: 'Pause' } : { key: 'activate', icon: <PlayCircleOutlined />, label: s.status === 'DRAFT' ? 'Activate' : 'Resume' },
                      { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate' },
                      ...(s.status !== 'ARCHIVED' ? [{ key: 'archive', icon: <InboxOutlined />, label: 'Archive' }] : []),
                      { type: 'divider' as const },
                      { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
                    ],
                    onClick: ({ key, domEvent }) => {
                      domEvent.stopPropagation();
                      if (key === 'pause' || key === 'activate' || key === 'archive')
                        status.mutateAsync({ id: s.id, action: key }).then(() => message.success(key === 'pause' ? 'Sequence paused' : key === 'archive' ? 'Sequence archived' : 'Sequence is running')).catch((e) => message.error(errorMessage(e)));
                      if (key === 'duplicate') duplicate.mutateAsync(s.id).then((d) => navigate(`/cold-email/${d.id}`)).catch((e) => message.error(errorMessage(e)));
                      if (key === 'delete')
                        modal.confirm({
                          title: `Delete sequence "${s.name}"?`,
                          content: 'Everyone still in it stops getting emails. Emails already sent and their replies stay in the Inbox.',
                          okText: 'Delete',
                          okButtonProps: { danger: true },
                          onOk: () => remove.mutateAsync(s.id).then(() => message.success('Sequence deleted')).catch((e) => message.error(errorMessage(e))),
                        });
                    },
                  }}
                >
                  <Button type="text" size="small" icon={<EllipsisOutlined style={{ fontSize: 18 }} />} aria-label={`Actions for ${s.name}`} onClick={(e) => e.stopPropagation()} />
                </Dropdown>
              ),
            },
          ]}
        />
      </Card>
      <Modal title="New sequence" open={creating} onCancel={() => setCreating(false)} okText="Create" confirmLoading={create.isPending} onOk={() => void submit()} destroyOnHidden>
        <Form form={form} layout="vertical" preserve={false} onFinish={() => void submit()}>
          <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Name the sequence' }]} extra="Only you see this, e.g. “SaaS founders — Q4”.">
            <Input autoFocus maxLength={160} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
